import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { Gamepad2, User } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useAuth } from '@/features/auth/contexts/AuthContext'
import {
  LoginModal,
  RegisterModal,
  ForgotPasswordModal,
} from '@/features/auth/components'

type ModalType = 'login' | 'register' | 'forgot' | null

export default function Landing() {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { isAuthenticated, isLoading } = useAuth()
  const [activeModal, setActiveModal] = useState<ModalType>(null)

  // If already authenticated, redirect to lobby
  if (isAuthenticated && !isLoading) {
    navigate('/lobby')
    return null
  }

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  }

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-4 relative overflow-hidden bg-gradient-to-b from-zinc-900 via-zinc-900 to-black">
      {/* Content */}
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="relative z-10 text-center max-w-xl mx-auto w-full"
      >
        {/* Logo */}
        <motion.div variants={itemVariants} className="mb-6">
          <motion.div
            whileHover={{ scale: 1.02 }}
            className="w-64 h-64 mx-auto"
          >
            <img
              src="/img/logo.png"
              alt="Cosa Nostra Casino"
              className="w-full h-full object-contain drop-shadow-[0_0_30px_rgba(212,175,55,0.3)]"
            />
          </motion.div>
        </motion.div>

        {/* PRIVATE CASINO with decorative lines */}
        <motion.div variants={itemVariants} className="flex items-center justify-center gap-4 mb-4">
          <div className="h-px w-16 bg-gradient-to-r from-transparent to-casino-gold/60" />
          <span className="font-cinzel text-sm tracking-[0.3em] text-casino-gold/80">
            {t('landing.casinoPrivate')}
          </span>
          <div className="h-px w-16 bg-gradient-to-l from-transparent to-casino-gold/60" />
        </motion.div>

        {/* Main heading */}
        <motion.h1
          variants={itemVariants}
          className="font-cinzel text-3xl md:text-4xl text-casino-gold font-bold italic mb-6"
        >
          {t('landing.experienceExclusive')}
        </motion.h1>

        {/* Welcome text */}
        <motion.p
          variants={itemVariants}
          className="text-gray-300 text-base mb-8 leading-relaxed px-4"
          dangerouslySetInnerHTML={{ __html: t('landing.welcomeMessage') }}
        />

        {/* INVITATION ONLY ACCESS button */}
        <motion.div variants={itemVariants} className="mb-4">
          <div className="bg-gradient-to-r from-casino-gold/20 via-casino-gold/30 to-casino-gold/20 border border-casino-gold/40 rounded-lg py-3 px-6">
            <span className="text-casino-gold tracking-[0.2em] text-sm font-medium flex items-center justify-center gap-2">
              <span className="text-casino-gold">&#9830;</span>
              {t('landing.accessInvitation')}
              <span className="text-casino-gold">&#9830;</span>
            </span>
          </div>
        </motion.div>

        {/* Membership text */}
        <motion.p variants={itemVariants} className="text-gray-400 text-sm mb-6">
          {t('landing.membersExclusive')}
        </motion.p>

        {/* Entertainment notice */}
        <motion.div
          variants={itemVariants}
          className="bg-zinc-800/50 border border-zinc-700 rounded-lg py-3 px-4 mb-6 flex items-center justify-center gap-3"
        >
          <span className="text-2xl">🎰</span>
          <span
            className="text-gray-300 text-sm"
            dangerouslySetInnerHTML={{ __html: t('landing.entertainmentNotice') }}
          />
        </motion.div>

        {/* Try Demo button - opens register modal */}
        <motion.div variants={itemVariants} className="mb-8">
          <Button
            variant="outline"
            size="lg"
            onClick={() => setActiveModal('register')}
            className="w-full bg-zinc-800/80 border-zinc-600 hover:bg-zinc-700 hover:border-zinc-500 py-6"
          >
            <Gamepad2 className="w-6 h-6 mr-3 text-casino-gold" />
            <div className="text-left">
              <div className="font-semibold text-white">{t('landing.tryDemo')}</div>
              <div className="text-xs text-gray-400">{t('landing.playFree')}</div>
            </div>
          </Button>
        </motion.div>

        {/* MEMBERS divider */}
        <motion.div variants={itemVariants} className="flex items-center justify-center gap-4 mb-6">
          <div className="h-px flex-1 bg-gradient-to-r from-transparent to-zinc-600" />
          <span className="text-xs tracking-[0.2em] text-gray-500">{t('landing.members')}</span>
          <div className="h-px flex-1 bg-gradient-to-l from-transparent to-zinc-600" />
        </motion.div>

        {/* Log In button */}
        <motion.div variants={itemVariants} className="mb-4">
          <Button
            variant="outline"
            size="lg"
            onClick={() => setActiveModal('login')}
            className="w-full bg-zinc-800/50 border-zinc-700 hover:bg-zinc-700 py-6"
          >
            <User className="w-5 h-5 mr-3 text-gray-400" />
            <div className="text-left">
              <div className="font-semibold text-white">{t('auth.login')}</div>
              <div className="text-xs text-gray-400">{t('auth.accessAccount')}</div>
            </div>
          </Button>
        </motion.div>

        {/* Request Membership button */}
        <motion.div variants={itemVariants} className="mb-8">
          <Button
            variant="gold"
            size="lg"
            onClick={() => setActiveModal('register')}
            className="w-full py-6"
          >
            <div className="text-center">
              <div className="font-semibold">{t('auth.requestMembership')}</div>
              <div className="text-xs opacity-80">{t('auth.joinCommunity')}</div>
            </div>
          </Button>
        </motion.div>

        {/* Footer text */}
        <motion.p variants={itemVariants} className="text-gray-500 text-xs">
          {t('landing.responsibleGaming')}
        </motion.p>
      </motion.div>

      {/* Auth Modals */}
      <LoginModal
        isOpen={activeModal === 'login'}
        onClose={() => setActiveModal(null)}
        onSwitchToRegister={() => setActiveModal('register')}
        onForgotPassword={() => setActiveModal('forgot')}
      />

      <RegisterModal
        isOpen={activeModal === 'register'}
        onClose={() => setActiveModal(null)}
        onSwitchToLogin={() => setActiveModal('login')}
      />

      <ForgotPasswordModal
        isOpen={activeModal === 'forgot'}
        onClose={() => setActiveModal(null)}
        onBackToLogin={() => setActiveModal('login')}
      />
    </div>
  )
}
